export type SubjectTagKey = 'ela' | 'math' | 'science' | 'social' | 'art' | 'other' | 'none'

/**
 * Subject is free text, not an enum, so this classifies by keyword rather
 * than exact match. Anything unrecognised falls back to 'other' rather than
 * going unstyled -- a purely decorative grouping for the subject pill color,
 * not a data classification.
 *
 * No subject at all is 'none', NOT 'other'. The mockups keep these distinct
 * ("Other" is a subject a teacher chose; blank is one she hasn't), and both
 * AllForOne_MyClassrooms_MintFade_v2.html and AllForOne_ClassroomDetail_v2.html
 * map `'': 'tag-none'` with its own colour. We previously painted blank with
 * the 'other' grey, which is close but reads as a real choice.
 */
export const classifySubject = (subject: string): SubjectTagKey => {
	const s = (subject || '').trim().toLowerCase()
	if (!s) return 'none'
	if (/english|ela|reading|writing|literacy/.test(s)) return 'ela'
	if (/math/.test(s)) return 'math'
	if (/science|biology|chemistry|physics/.test(s)) return 'science'
	if (/social studies|history|geography|civics/.test(s)) return 'social'
	if (/art|music|drama|theater|theatre/.test(s)) return 'art'
	return 'other'
}

/**
 * Maps the pure classification to this app's global subject-pill CSS
 * classes (plain CSS, not a module -- see classroom-card.css). Shared by
 * every subject pill (classroom cards, dashboard lesson cards, classroom
 * detail) so the color language stays in one place.
 */
export const SUBJECT_TAG_CLASS: Record<SubjectTagKey, string> = {
	ela: 'card-tag-ela',
	math: 'card-tag-math',
	science: 'card-tag-science',
	social: 'card-tag-social',
	art: 'card-tag-art',
	other: 'card-tag-other',
	none: 'card-tag-none',
}
